import React, {useEffect, useState} from 'react';
import Select from 'react-select';
import Loading from './../misc/Loading';
import assetModel from './../../../../model/asset';
import TagModel from './../../../../model/tag';
import {NavLink as Link, useLocation, useNavigate} from 'react-router-dom';
import PublishStatusTableRow from '../table/PublishStatusTableRow';
import ImageResponsive from '../../../contentful/ImageResponsive';
import FileIcon from '../../../misc/FileIcon';
import queryString from 'query-string';
import slug from 'slug';

const ITEMS_PER_PAGE = 20;
const QUERY_STRING_DELIMITER = '§';
const FILETYPE_FILTERS = [
  {
    label: 'Alle filtyper',
    value: ''
  },
  {
    label: 'PDF',
    value: 'pdfdocument'
  },
  {
    label: 'Bilder',
    value: 'image'
  },
  {
    label: 'Video',
    value: 'video'
  }
];

const MediaBrowserList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [working, setWorking] = useState(true);
  const [hideArchived, setHideArchived] = useState(true);
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [searchFilter, setSearchFilter] = useState('');
  const [page, setPage] = useState(1);
  const [filetypeFilter, setFiletypeFilter] = useState('');
  const [tagsMap, setTagsMap] = useState({});
  const [tagSelectOptions, setTagSelectOptions] = useState([]);
  const [selectedFilterTags, setSelectedFilterTags] = useState([]);

  const keyTags = (tags) => {
    const keyedTags = tags.reduce((res, item) => {
      res[item.id] = item.name;

      return res;
    }, {});

    return keyedTags;
  };

  const enrichTags = (tags) => {
    // We only receive the tag ID from the API, so we need to enrich it with the name taken from the tags.
    const enrichedTags = tags.reduce((res, tag) => {
      const name = tagsMap[tag.id] ?? null;
      if (name === null) {
        return res;
      }

      res.push({
        id: tag.id,
        name: name
      });

      return res;
    }, []);

    return enrichedTags;
  };

  const getMedia = async () => {
    const params = {
      includeArchived: !hideArchived
    };

    if (searchFilter !== '') {
      params['searchFilter'] = searchFilter;
    }

    if (filetypeFilter !== '') {
      params['mimetypeGroup'] = filetypeFilter;
    }

    if (selectedFilterTags.length > 0) {
      params['tags'] = selectedFilterTags.map((tag) => tag.value).join(',');
    }


    const offset = (page - 1) * ITEMS_PER_PAGE;

    const [fetchedItems, fetchedTotalItems] = await assetModel.allByParams(offset, ITEMS_PER_PAGE, params);
    const tags = await TagModel.getAll();

    // Check if the assets shown are in use.
    const assetIDs = fetchedItems.map((asset) => asset.id);
    const assetStatus = await assetModel.getAssetStatus(assetIDs);

    // Append the status to each asset we have received.
    for (let i = 0; i < fetchedItems.length; i++) {
      // Null is used to show that we have no info about the status,
      // giving the user an appropriate message. This will normally happen
      // with new images that is added before a new run is done.
      fetchedItems[i].status = assetStatus[fetchedItems[i].id] ?? null;
    }

    setTotalItems(fetchedTotalItems);
    setTagsMap(keyTags(tags));
    setTagSelectOptions(tags.map((tag) => ({label: tag.name, value: tag.id})));
    setItems(fetchedItems);
  };

  useEffect(() => {
    // Keep the title updated, as everylocation change will reset it to default
    document.title = 'Admin - Mediautforsker';
  }, [location]);

  useEffect(() => {
    if (!location.search) {
      return;
    }

    const {
      page = 0,
      q = '',
      file_type_filter: fileTypeFilter,
      hide_archived: hideArchived = 'true',
      tags = ''
    } = queryString.parse(location.search);

    const numberPage = Number(page);
    if (numberPage && numberPage > 0) {
      setPage(numberPage);
    }
    if (q && q !== '') {
      setSearchFilter(q);
    }
    if (fileTypeFilter) {
      setFiletypeFilter(fileTypeFilter);
    }
    setHideArchived(hideArchived === 'true');
    if (tags) {
      setSelectedFilterTags(tags.split(QUERY_STRING_DELIMITER).map((tag) => {
        // We assume that the value is the label sluggified. This might not always be the case.
        // TODO: Find a better solution than this for handling query strings before the global tags might have been loaded.
        return {
          label: tag,
          value: slug(tag)
        };
      }));
    }
  }, []);

  useEffect(() => {
    const getUpdated = async () => {
      await getMedia();
      setWorking(false);
    };
    getUpdated();
  }, [page, filetypeFilter, hideArchived, selectedFilterTags]);

  const updateQueryString = () => {
    navigate({
      search: queryString.stringify({
        q: searchFilter,
        page: page,
        hide_archived: hideArchived,
        file_type_filter: filetypeFilter,
        tags: selectedFilterTags.map((tag) => tag.label).join(QUERY_STRING_DELIMITER)
      }, {replace: true})
    });
  };

  useEffect(() => {
    updateQueryString();
  }, [page, filetypeFilter, hideArchived, selectedFilterTags]);

  const toggleShowArchived = () => {
    setHideArchived((prev) => !prev);
    // Reset page to 1 as a filter has changed.
    setPage(1);
  };

  const handleFiletypeFilterSelect = (item) => {
    setFiletypeFilter(item.value);
    // Reset page to 1 as a filter has changed.
    setPage(1);
  };

  const handleTagFilterSelect = (items) => {
    setSelectedFilterTags(items);
    // Reset page to 1 as a filter has changed.
    setPage(1);
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    // Reset page to 1 as a filter has changed.
    setPage(1);

    getMedia();
    updateQueryString();
  };

  const printPager = (page) => {
    if (!items.length || items.length === 0) {
      return;
    }

    if (!totalItems || totalItems <= ITEMS_PER_PAGE) {
      return;
    }

    const pages = Math.ceil(totalItems / ITEMS_PER_PAGE);

    const pageItems = [];

    for (let i = 0; i < pages; i++) {
      // Only show the page two below and two above the current page.
      const currentPage = i + 1;
      if ((currentPage < page - 2) || (currentPage > page + 2)) {
        continue;
      }

      const pageItem = (
        <li key={`page-${i}`} className={page === currentPage ? 'active' : (working ? ' disabled' : '')}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setPage(currentPage);
            }}
            disabled={working}
          >
            {i + 1}
          </a>
        </li>
      );

      pageItems.push(pageItem);
    }

    if (totalItems && totalItems > ITEMS_PER_PAGE) {
      return <ul className="pagination">
        <li className={(page === 1 || working) ? 'disabled' : ''}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setPage(1);
            }}
            aria-label="Første side"
            disabled={working}
          >
            <span aria-hidden="true">&laquo;</span>
          </a>
        </li>

        {pageItems}

        <li className={(page >= pages || working) ? 'disabled' : ''}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setPage(pages);
            }}
            aria-label="Siste side"
            disabled={working}>
            <span aria-hidden="true">&raquo;</span>
          </a>
        </li>
      </ul>;
    }
  };

  const createThumbnail = (asset) => {
    // If we have an image, show the thumbnail, otherwise a file placeholder.
    if (asset.contentType.startsWith('image/')) {
      return (
        <div className="contentful-asset-image contentful-asset-toolbar-bottom">
          <ImageResponsive
            className="img-rounded"
            src={asset.url}
            width={200}
            height={200}
            fit="pad"
          />
        </div>
      );
    }

    return (
      <div style={{fontSize: 33}}>
        <FileIcon className="contentful-asset-icon" contentType={asset.contentType} />
      </div>
    );
  };

  const createStatusIcon = (status) => {
    if (status === true) {
      return (
        <i
          className="fa fa-check-circle text-success"
          style={{fontSize: 33}}
          aria-hidden="true"
          title="Medieelement er i bruk"
        />
      );
    }
    if (status === false) {
      return (
        <i
          className="fa fa-times text-danger"
          style={{fontSize: 33}}
          aria-hidden="true"
          title="Medieelement er IKKE i bruk"
        />
      );
    }

    return (
      <i
        className="fa fa-minus-circle text-muted"
        style={{fontSize: 33}}
        aria-hidden="true"
        title="Har ingen status på dette objektet. Nytt bilde/fil?"
      />
    );
  };

  if (working) {
    return (
      <Loading title="Mediautforsker" text="Laster media" />
    );
  }

  const filetypeFilterDefault = (filetypeFilter) ? (FILETYPE_FILTERS.find((filetype) => filetype.value === filetypeFilter) ?? FILETYPE_FILTERS[0]) : FILETYPE_FILTERS[0];

  return (
    <section>
      <div className="mf-model">
        <ol className="breadcrumb mf-margin-top-20">
          <li><Link to="/">Forsiden</Link></li>
          <li className="active">Mediautforsker</li>
        </ol>
        <div style={{display: 'flex'}}>
          <h1 style={{display: 'flex', flex: 1}}>{`Mediautforsker (${totalItems})`}</h1>
        </div>
        <div className="pull-right" style={{display: 'flex'}}>
          <div style={{display: 'flex', flex: 1, flexDirection: 'row'}}>
            <div className="form-group" style={{display: 'flex', flex: 1, flexDirection: 'row'}}>
              <form onSubmit={onFormSubmit}>
                <div style={{minWidth: 150}}>
                  <input type="text" className="form-control" style={{height: 38}} placeholder="Søk" onChange={(e) => setSearchFilter(e.target.value)} defaultValue={searchFilter} />
                </div>
              </form>
              <div style={{minWidth: 400, marginRight: 5, marginLeft: 5}}>
                <Select options={tagSelectOptions} isMulti={true} isSearchable={true} onChange={handleTagFilterSelect} placeholder="Filtrer på tag" defaultValue={selectedFilterTags} />
              </div>
              <div style={{minWidth: 200, marginRight: 5}}>
                <Select options={FILETYPE_FILTERS} onChange={handleFiletypeFilterSelect} defaultValue={filetypeFilterDefault}/>
              </div>
              <button onClick={toggleShowArchived} className="btn btn-info" disabled={working} style={{height: 40}}>
                {hideArchived ? 'Vis arkiverte' : 'Skjul arkiverte'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th width={150}>Forhåndsvisning</th>
                <th>Tittel og beskrivelse</th>
                <th>Filnavn</th>
                <th>Tags</th>
                <th>Filtype</th>
                <th>Opprettet</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item) => {
                if (hideArchived && item.archived) {
                  return;
                }

                return (
                  <PublishStatusTableRow entry={item} key={`mediaItem-${item.id}`}>
                    <td className="text-center">
                      <Link to={`/media/${item.id}`}>
                        {createThumbnail(item)}
                      </Link>
                    </td>
                    <td>
                      <Link to={`/media/${item.id}`}>{item.title}</Link><br/>
                      {item.description &&
                        <span>{item.description.substring(0, 100)}{item.description.length > 100 && <span>...</span>}</span>
                      }
                    </td>
                    <td>{item.fileName}</td>
                    <td>
                      {enrichTags(item.tags).map((tag) => (
                        <span key={`tag-${tag.id}`} className="badge badge-light">{tag.name}</span>
                      ))}
                    </td>
                    <td>{item.contentType}</td>
                    <td>{item.createdAt.toLocaleString('nb')}</td>
                    <td>{createStatusIcon(item.status)}</td>
                  </PublishStatusTableRow>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-center">
        <p>Page: {page}</p>
        {printPager(page)}
      </div>
    </section>
  );
};

MediaBrowserList.defaultProps = {
};

export default MediaBrowserList;
